import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { rgbToHex } from "@mui/material/styles";
import { rgbToCSS } from "common/utilities/colors/colors";
import type { Color } from "common/utilities/colors/colors.types";
import { arrayEquals, arrayFlat } from "common/utilities/core/array";
import React from "react";
import { saveSequenceFeatureColor } from "sequenceAnalyser/api/api";
import type { Sequence, SequenceFeatureColor, SequenceType } from "sequenceAnalyser/api/sequenceAnalyserApiClient";
import { SequenceFeatureTypeColorSetting } from "sequenceAnalyser/api/sequenceAnalyserApiClient";
import FeatureDefinitionTable from "sequenceAnalyser/components/Features/FeatureDefinitionTable/FeatureDefinitionTable";
import FeaturesTree from "sequenceAnalyser/components/Features/FeaturesTree/FeaturesTree";
import type { HandleFeatureDefinitionsChange } from "sequenceAnalyser/components/SequenceAnalyser/SequenceAnalyser";
import {
  areAllFeaturesDefinitionsHidden,
  createFeatureDefinitions,
  groupFeaturesDefinitionByType,
} from "sequenceAnalyser/utilities/features/features";
import { defaultFeatureTypesColorSetting } from "sequenceAnalyser/utilities/featuresColors/defaults";
import type {
  FeatureDefinition,
  FeatureDefinitions,
  FeatureId,
  FeatureTypesColor,
  SequenceFeaturesType,
  SequenceFeatureWithRef,
} from "sequenceAnalyser/utilities/sequenceAnalyser.types";

interface ComponentProps {
  sequenceType: SequenceType;
  userColors: SequenceFeatureColor[];
  featureDefinitions: FeatureDefinitions;
  featureTypesColor: FeatureTypesColor;
  onChange: HandleFeatureDefinitionsChange;
  tableView?: boolean;
  sequences: Sequence[];
}

interface ComponentState {
  features: SequenceFeatureWithRef[];
  featureTypes: SequenceFeaturesType[];
}

function buildSequenceFeatureType(
  userColors: SequenceFeatureColor[],
  featureDefinitions: FeatureDefinitions,
  featureTypesColor: FeatureTypesColor,
) {
  return groupFeaturesDefinitionByType(featureDefinitions, userColors, featureTypesColor).sort((a, b) =>
    a.type.localeCompare(b.type),
  );
}

class Features extends React.Component<ComponentProps, ComponentState> {
  public constructor(props: ComponentProps) {
    super(props);

    this.state = {
      features: [],
      featureTypes: buildSequenceFeatureType(props.userColors, props.featureDefinitions, props.featureTypesColor),
    };
  }

  componentDidMount() {
    this.handleSelectAllFeatures();
  }

  getAllFeatures() {
    const { featureDefinitions, featureTypesColor, userColors } = this.props;
    const groups = buildSequenceFeatureType(userColors, featureDefinitions, featureTypesColor);

    return arrayFlat<SequenceFeatureWithRef>(
      groups.map((g) => arrayFlat<SequenceFeatureWithRef>(g.definitions.map((d) => d.features))),
    );
  }

  handleSelectAllFeatures = () => {
    this.setState({ features: this.getAllFeatures() });
  };

  handleSelectFeatures = (features: SequenceFeatureWithRef[]) => {
    this.setState({ features });
  };

  handleVisibilityChange = (id: FeatureId) => {
    const { featureDefinitions, featureTypesColor, userColors, onChange } = this.props;

    const newFeatureDefinitions: FeatureDefinitions = {
      ...featureDefinitions,
      [id]: {
        ...featureDefinitions[id],
        visible: !featureDefinitions[id].visible,
      },
    };

    onChange(newFeatureDefinitions, featureTypesColor, userColors);
  };

  handleFeatureDefinitionsVisibilityChange = (definitions: FeatureDefinition[]) => {
    const { featureDefinitions, featureTypesColor, userColors, onChange } = this.props;

    const ids: FeatureId[] = definitions.map((def) => {
      const id: FeatureId = def.uniqueTypeString;
      return id;
    });

    // When toggling group of features definitions visibility:
    // if ALL are hidden, toggle will show them all
    // otherwise the toggle will hide them all
    const newVisible = areAllFeaturesDefinitionsHidden(definitions);

    const newFeatureDefinitions: FeatureDefinitions = (
      Object.keys(featureDefinitions) as FeatureId[]
    ).reduce<FeatureDefinitions>((acc, definition) => {
      acc[definition] = {
        ...featureDefinitions[definition],
        visible: ids.includes(definition) ? newVisible : featureDefinitions[definition].visible,
      };
      return acc;
    }, {});

    onChange(newFeatureDefinitions, featureTypesColor, userColors);
  };

  handleColorChange = async (typeId: number, label: string, color: Color | undefined) => {
    const { sequences, featureDefinitions, featureTypesColor, onChange, sequenceType } = this.props;

    const userColors = await saveSequenceFeatureColor({
      sequenceType,
      featureTypeId: typeId,
      label,
      color: color ? rgbToHex(rgbToCSS(color)) : undefined,
    });

    const output = createFeatureDefinitions(sequences, userColors, featureDefinitions, featureTypesColor);

    // update and re-create using user colors
    onChange(output.featureDefinitions, output.featureTypesColor, userColors);
  };

  handleFeatureTypeChange = async (
    sequenceFeatureType: SequenceFeaturesType,
    setting: SequenceFeatureTypeColorSetting,
    color?: Color,
  ) => {
    const { sequences, featureDefinitions, featureTypesColor, onChange, sequenceType } = this.props;

    // Only send the setting if it's not the default 'I' setting
    let newSetting: SequenceFeatureTypeColorSetting | undefined = setting;
    if (
      !defaultFeatureTypesColorSetting[sequenceFeatureType.type] &&
      newSetting === SequenceFeatureTypeColorSetting.I
    ) {
      newSetting = undefined;
    }

    // Only send the color if it's not hardcoded in this project
    let newColor: string | undefined;
    if (color) {
      if (!arrayEquals(color, sequenceFeatureType.color)) {
        newColor = rgbToHex(rgbToCSS(color));
      } else {
        newColor =
          featureTypesColor[sequenceFeatureType.type].source === "hardcoded"
            ? undefined
            : rgbToHex(rgbToCSS(sequenceFeatureType.color));
      }
    }

    const userColors = await saveSequenceFeatureColor({
      sequenceType,
      featureTypeId: sequenceFeatureType.typeId,
      color: newColor,
      setting: newSetting,
    });

    const output = createFeatureDefinitions(sequences, userColors, featureDefinitions, featureTypesColor);

    this.setState(
      {
        featureTypes: buildSequenceFeatureType(userColors, output.featureDefinitions, output.featureTypesColor),
      },
      () => {
        onChange(output.featureDefinitions, output.featureTypesColor, userColors);
      },
    );
  };

  handleClearFeatureType = async (sequenceFeatureType: SequenceFeaturesType) => {
    const { sequences, featureDefinitions, featureTypesColor, onChange, sequenceType } = this.props;

    const userColors = await saveSequenceFeatureColor({
      sequenceType,
      featureTypeId: sequenceFeatureType.typeId,
      clearAll: true,
    });

    const output = createFeatureDefinitions(sequences, userColors, featureDefinitions, featureTypesColor);

    this.setState(
      {
        featureTypes: buildSequenceFeatureType(userColors, output.featureDefinitions, output.featureTypesColor),
      },
      () => {
        onChange(output.featureDefinitions, output.featureTypesColor, userColors);
      },
    );
  };

  public render() {
    const { tableView, featureDefinitions } = this.props;
    const { features, featureTypes } = this.state;

    const FeatureTreeRender = (
      <FeaturesTree
        featureDefinitions={Object.values(featureDefinitions)}
        featureTypes={featureTypes}
        onFeatureDefinitionsVisibilityChange={this.handleFeatureDefinitionsVisibilityChange}
        onSelectAll={this.handleSelectAllFeatures}
        onSelectFeatures={this.handleSelectFeatures}
        onVisibilityChange={this.handleVisibilityChange}
        onColorChange={this.handleColorChange}
        onFeatureTypeChange={this.handleFeatureTypeChange}
        onClearFeatureType={this.handleClearFeatureType}
        disableSelect={!tableView}
      />
    );

    if (!tableView) {
      return FeatureTreeRender;
    }

    return (
      <Grid container>
        <Grid item xs={3}>
          <Box pr={2}>{FeatureTreeRender}</Box>
        </Grid>
        <Grid item xs={9}>
          <Box pl={1} pr={1} mt={-0.5}>
            <FeatureDefinitionTable
              features={features}
              featureDefinitions={featureDefinitions}
              onColorChange={this.handleColorChange}
            />
          </Box>
        </Grid>
      </Grid>
    );
  }
}

export default Features;
